import { relations, sql } from 'drizzle-orm';
import {
  index,
  integer,
  jsonb,
  pgTable,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { user } from './user';
import { chatRoom } from './messaging';
import {
  callRoomType,
  callRoomStatus,
  callParticipantRole,
  callInvitationStatus,
  accessTargetType,
} from './enums';

// ==========================================
// CALL ROOM — комната видеозвонка (CALL) или стрима со сценой (STAGE)
// ==========================================
export const callRoom = pgTable('call_rooms', {
  id: uuid('id').primaryKey().defaultRandom(),
  type: callRoomType('type').notNull(),
  status: callRoomStatus('status').notNull().default('SCHEDULED'),
  // integer FK — User.id is generatedAlwaysAsIdentity integer.
  // Nullable: admin-инициированные комнаты (STAGE-сцена трансляции,
  // breakout-залы) не имеют host-юзера из таблицы User — их владелец admin.
  hostUserId: integer('host_user_id').references(() => user.id),
  livekitRoomName: varchar('livekit_room_name', { length: 128 }).notNull(),
  scheduledAt: timestamp('scheduled_at', { withTimezone: true }),
  startedAt: timestamp('started_at', { withTimezone: true }),
  endedAt: timestamp('ended_at', { withTimezone: true }),
  // Полиморфная привязка к сущности (курс, модуль, абонемент и т.п.)
  // integer FK — all target entities use integer PKs in prod
  accessTargetType: accessTargetType('access_target_type'),
  accessTargetId: integer('access_target_id'),
  // Необязательная привязка к чат-комнате (integer FK, совпадает с ChatRoom.id)
  chatRoomId: integer('chat_room_id').references(() => chatRoom.id, { onDelete: 'set null' }),
  createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
  uniqueIndex('call_rooms_livekit_room_name_idx').on(t.livekitRoomName),
  index('call_rooms_host_user_id_idx').on(t.hostUserId),
  index('call_rooms_status_idx').on(t.status),
]);

// ==========================================
// CALL PARTICIPANT — участник звонка
// ==========================================

export interface RoleHistoryEntry {
  role: string;
  changedAt: string;
}

export const callParticipant = pgTable('call_participants', {
  id: uuid('id').primaryKey().defaultRandom(),
  roomId: uuid('room_id').notNull().references(() => callRoom.id, { onDelete: 'cascade' }),
  // integer FK — User.id is generatedAlwaysAsIdentity integer
  userId: integer('user_id').notNull().references(() => user.id),
  role: callParticipantRole('role').notNull().default('VIEWER'),
  joinedAt: timestamp('joined_at', { withTimezone: true }).notNull().defaultNow(),
  leftAt: timestamp('left_at', { withTimezone: true }),
  roleHistory: jsonb('role_history').$type<RoleHistoryEntry[]>().notNull().default([]),
}, (t) => [
  // Один активный participant на юзера в комнате (left_at IS NULL)
  uniqueIndex('call_participants_active_unique')
    .on(t.roomId, t.userId)
    .where(sql`left_at IS NULL`),
  index('call_participants_room_id_idx').on(t.roomId),
  index('call_participants_user_id_idx').on(t.userId),
]);

// ==========================================
// CALL INVITATION — приглашение в звонок
// ==========================================
export const callInvitation = pgTable('call_invitations', {
  id: uuid('id').primaryKey().defaultRandom(),
  roomId: uuid('room_id').notNull().references(() => callRoom.id, { onDelete: 'cascade' }),
  // integer FKs — User.id is generatedAlwaysAsIdentity integer
  inviterUserId: integer('inviter_user_id').notNull().references(() => user.id),
  inviteeUserId: integer('invitee_user_id').notNull().references(() => user.id),
  status: callInvitationStatus('status').notNull().default('PENDING'),
  expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
  respondedAt: timestamp('responded_at', { withTimezone: true }),
  createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
  index('call_invitations_room_id_idx').on(t.roomId),
  index('call_invitations_invitee_user_id_idx').on(t.inviteeUserId),
  index('call_invitations_status_idx').on(t.status),
]);

// ==========================================
// RELATIONS
// ==========================================

export const callRoomRelations = relations(callRoom, ({ one, many }) => ({
  host: one(user, { fields: [callRoom.hostUserId], references: [user.id] }),
  chatRoom: one(chatRoom, { fields: [callRoom.chatRoomId], references: [chatRoom.id] }),
  participants: many(callParticipant),
  invitations: many(callInvitation),
}));

export const callParticipantRelations = relations(callParticipant, ({ one }) => ({
  room: one(callRoom, { fields: [callParticipant.roomId], references: [callRoom.id] }),
  user: one(user, { fields: [callParticipant.userId], references: [user.id] }),
}));

export const callInvitationRelations = relations(callInvitation, ({ one }) => ({
  room: one(callRoom, { fields: [callInvitation.roomId], references: [callRoom.id] }),
  inviter: one(user, { fields: [callInvitation.inviterUserId], references: [user.id] }),
  invitee: one(user, { fields: [callInvitation.inviteeUserId], references: [user.id] }),
}));
